import axios from 'axios';
import { getAuthToken } from '../utils/authUtils';
import { getApiBaseUrl } from '../utils/apiConfig';

const API_URL = getApiBaseUrl();

// Create axios instance with auth header
const authAxios = axios.create({
  baseURL: `${API_URL}/timeslots`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add authorization header to every request
authAxios.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
authAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Time Slot Service
 * Handles all API calls related to professional time slot management
 */
const timeSlotService = {
  /**
   * Get the current professional's time slots
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - Filter from start date (YYYY-MM-DD)
   * @param {string} params.endDate - Filter to end date (YYYY-MM-DD)
   * @param {string} params.status - Filter by status (available, booked, cancelled)
   * @returns {Promise} API response with time slots array
   */
  getMyTimeSlots: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.status) queryParams.append('status', params.status);
      
      const url = `/my-slots${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      console.log('📤 Fetching time slots with URL:', `${API_URL}/timeslots${url}`);
      
      const response = await authAxios.get(url);
      console.log('✅ Time slots fetch response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching time slots:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        }
      });
      throw error;
    }
  },

  /**
   * Create a new time slot
   * @param {Object} slotData - Time slot data
   * @param {string} slotData.date - Slot date (YYYY-MM-DD)
   * @param {string} slotData.startTime - Start time (HH:mm)
   * @param {string} slotData.endTime - End time (HH:mm)
   * @param {number} slotData.duration - Duration in minutes
   * @param {string} slotData.notes - Optional notes
   * @param {number} slotData.rateOverride - Optional rate override
   * @param {string} slotData.timezone - Timezone (default: UTC)
   * @returns {Promise} API response with created slot
   */
  createTimeSlot: async (slotData) => {
    try {
      console.log('📤 Creating time slot with data:', JSON.stringify(slotData, null, 2));
      console.log('📤 Request URL:', `${API_URL}/timeslots`);
      
      const response = await authAxios.post('/', slotData);
      console.log('✅ Time slot create response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating time slot:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        }
      });
      throw error;
    }
  },

  /**
   * Update an existing time slot
   * @param {string} slotId - Time slot ID
   * @param {Object} updateData - Updated slot data
   * @returns {Promise} API response with updated slot
   */
  updateTimeSlot: async (slotId, updateData) => {
    try {
      console.log('📤 Updating time slot:', slotId, 'with data:', JSON.stringify(updateData, null, 2));
      
      const response = await authAxios.put(`/${slotId}`, updateData);
      console.log('✅ Time slot update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating time slot:', {
        slotId,
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      throw error;
    }
  },

  /**
   * Delete a time slot
   * @param {string} slotId - Time slot ID
   * @returns {Promise} API response
   */
  deleteTimeSlot: async (slotId) => {
    try {
      console.log('📤 Deleting time slot:', slotId);
      
      const response = await authAxios.delete(`/${slotId}`);
      console.log('✅ Time slot delete response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting time slot:', {
        slotId,
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      throw error;
    }
  },

  /**
   * Get available time slots for a professional (public endpoint)
   * @param {string} professionalId - Professional's user ID
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - Filter from start date (YYYY-MM-DD)
   * @param {string} params.endDate - Filter to end date (YYYY-MM-DD)
   * @returns {Promise} API response with available slots
   */
  getAvailableSlots: async (professionalId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      
      const url = `/professional/${professionalId}/available${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      console.log('📤 Fetching available slots with URL:', `${API_URL}/timeslots${url}`);
      
      // Use regular axios for public endpoint (no auth required)
      const response = await axios.get(`${API_URL}/timeslots${url}`);
      console.log('✅ Available slots fetch response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching available slots:', {
        professionalId,
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      throw error;
    }
  },

  /**
   * Create multiple time slots in bulk
   * @param {Array} slotsData - Array of time slot data objects
   * @returns {Promise} API response with created slots
   */
  bulkCreateTimeSlots: async (slotsData) => {
    try {
      console.log('📤 Bulk creating time slots:', JSON.stringify(slotsData, null, 2));
      
      const response = await authAxios.post('/bulk', { slots: slotsData });
      console.log('✅ Bulk create response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error bulk creating time slots:', {
        slotsCount: slotsData?.length,
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      throw error;
    }
  },

  /**
   * Get time slot statistics for the current professional
   * @returns {Promise} API response with statistics
   */
  getTimeSlotStats: async () => {
    try {
      const response = await authAxios.get('/stats');
      console.log('✅ Time slot stats response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching time slot stats:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      throw error;
    }
  },

  /**
   * Helper: Calculate duration between start and end time
   * @param {string} startTime - Start time (HH:mm)
   * @param {string} endTime - End time (HH:mm)
   * @returns {number} Duration in minutes
   */
  calculateDuration: (startTime, endTime) => {
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    const startTotalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = endHour * 60 + endMinute;
    
    return endTotalMinutes - startTotalMinutes;
  },

  /**
   * Helper: Format time slot for calendar display
   * @param {Object} slot - Raw time slot from API
   * @returns {Object} Formatted slot for react-big-calendar
   */
  formatSlotForCalendar: (slot) => {
    const slotDate = new Date(slot.date);
    const [startHour, startMinute] = slot.startTime.split(':').map(Number);
    const [endHour, endMinute] = slot.endTime.split(':').map(Number);
    
    const start = new Date(slotDate);
    start.setHours(startHour, startMinute, 0, 0);
    
    const end = new Date(slotDate);
    end.setHours(endHour, endMinute, 0, 0);
    
    return {
      id: slot._id,
      title: slot.status === 'available' ? 'Available' : slot.status,
      start,
      end,
      resource: {
        timeSlotId: slot._id,
        status: slot.status,
        notes: slot.notes,
        rateOverride: slot.rateOverride,
        isRecurring: slot.notes && slot.notes.includes('SeriesId:'),
        seriesId: slot.notes ? slot.notes.match(/SeriesId:([^|]+)/)?.[1] : null
      }
    };
  },

  /**
   * Helper: Validate time slot data
   * @param {Object} slotData - Time slot data to validate
   * @returns {Object} Validation result { isValid, errors }
   */
  validateTimeSlot: (slotData) => {
    const errors = [];

    if (!slotData.date || !/^\d{4}-\d{2}-\d{2}$/.test(slotData.date)) {
      errors.push('Valid date is required (YYYY-MM-DD format)');
    }

    if (!slotData.startTime || !/^([01]\d|2[0-3]):([0-5]\d)$/.test(slotData.startTime)) {
      errors.push('Valid start time is required (HH:MM format)');
    }

    if (!slotData.endTime || !/^([01]\d|2[0-3]):([0-5]\d)$/.test(slotData.endTime)) {
      errors.push('Valid end time is required (HH:MM format)');
    }

    if (slotData.startTime && slotData.endTime && slotData.startTime >= slotData.endTime) {
      errors.push('End time must be after start time');
    }

    if (slotData.duration !== undefined) {
      if (typeof slotData.duration !== 'number' || slotData.duration < 15 || slotData.duration > 480) {
        errors.push('Duration must be between 15 minutes and 8 hours');
      }
    }

    if (slotData.rateOverride !== null && slotData.rateOverride !== undefined) {
      if (typeof slotData.rateOverride !== 'number' || slotData.rateOverride < 0) {
        errors.push('Rate override must be a positive number');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

export default timeSlotService;